// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import * as Location from 'expo-location';
import { Ionicons } from '@expo/vector-icons';

import { HoleMapCanvas } from '@/features/hole/holeview/HoleMapCanvas';
import {
  IDENTITY_VIEW,
  MAX_VIEW_ZOOM,
  MIN_EDIT_ZOOM,
  type LatLon,
  type ViewTransform,
} from '@/features/hole/holeview/tileMath';
import type { TeePoint } from '@/features/hole/holeview/useHoleViewGeometry';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { formatLatLon, parseLatLon } from './coordinateInput';

export type LocationPickerMapProps = {
  /** Where to frame the map on open — the anchor the failed search used, or null if none. */
  initialCenter: LatLon | null;
  /** Searches OSM again, anchored on the picked point. */
  onConfirm: (point: LatLon) => void;
  /**
   * Takes the picked point on its own, with nothing else OSM offers — for a course OSM has no
   * data for at all, which is the reason the picker was opened in the first place.
   */
  onUseAsPosition: (point: LatLon) => void;
  onCancel: () => void;
  /** For the place-name search below — a town or address, not necessarily a golf course. */
  apiBaseUrl: string;
  token?: string | null;
};

/** One OpenStreetMap place returned by a name search — a town, address or landmark. */
type OsmPlaceMatch = {
  name: string;
  lat: number;
  lon: number;
  locality?: string | null;
};

/** How much ground to show around a known centre — enough to recognise a course by its shape. */
const PICKER_SPAN_METERS = 800;

/**
 * Shown only when there is no anchor at all to start from — a brand-new course with no club
 * position and no device fix. Wide enough to be a starting point from anywhere in the world,
 * which a course-sized span around (0, 0) would not be.
 */
const WORLD_FALLBACK_CENTER: LatLon = { lat: 20, lon: 0 };
const WORLD_FALLBACK_SPAN_METERS = 15_000_000;

const ZOOM_STEP = 1.6;

/**
 * There is nothing to place on this map, only ground to centre on — but a fresh `[]` literal
 * on every render would still count as a changed prop to `HoleMapCanvas`'s memoised layout,
 * which feeds `onCenterChange` below. A stable reference keeps a no-op render a no-op.
 */
const NO_TEES: TeePoint[] = [];

/**
 * A pannable, zoomable satellite view for picking the point an OSM search should be anchored
 * on, for when the automatic anchor — the club's stored position or the device's own — is
 * wrong or missing. Built from the same tile canvas and chrome the hole-geometry editor uses
 * (`HoleMapCanvas`, `CourseHoleMapModal`), stripped down to just panning under a fixed
 * crosshair: there is nothing here to click and place, only ground to centre on.
 */
export function LocationPickerMap({
  initialCenter,
  onConfirm,
  onUseAsPosition,
  onCancel,
  apiBaseUrl,
  token,
}: LocationPickerMapProps) {
  const { t } = useI18n();
  const [view, setView] = useState<ViewTransform>(IDENTITY_VIEW);
  const [center, setCenter] = useState<LatLon | null>(initialCenter);
  const [gotoText, setGotoText] = useState('');
  const [gotoInvalid, setGotoInvalid] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  /** The live coordinate under the crosshair, read back from the canvas as the view moves. */
  const [pickedPoint, setPickedPoint] = useState<LatLon | null>(initialCenter);
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeSearching, setPlaceSearching] = useState(false);
  const [placeError, setPlaceError] = useState<string | null>(null);
  /** Null before any search; an empty array is a search that genuinely found nothing. */
  const [placeResults, setPlaceResults] = useState<OsmPlaceMatch[] | null>(null);

  const hasRealCenter = center !== null;
  const frameCenter = center ?? WORLD_FALLBACK_CENTER;
  const frameSpan = hasRealCenter ? PICKER_SPAN_METERS : WORLD_FALLBACK_SPAN_METERS;

  const recentre = (point: LatLon) => {
    setCenter(point);
    setPickedPoint(point);
    setView(IDENTITY_VIEW);
  };

  const goToTypedCoordinate = () => {
    const point = parseLatLon(gotoText);
    if (!point) {
      setGotoInvalid(true);
      return;
    }
    setGotoInvalid(false);
    setLocateError(null);
    recentre(point);
  };

  const searchPlace = async () => {
    const query = placeQuery.trim();
    if (query.length < 3) {
      setPlaceError(t('courseConfig.osmMapPlaceTooShort'));
      return;
    }
    setPlaceSearching(true);
    setPlaceError(null);
    setPlaceResults(null);
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/geodata/osm/places?name=${encodeURIComponent(query)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      if (!response.ok) {
        setPlaceError(t('courseConfig.osmMapPlaceFailed'));
        return;
      }
      const body = (await response.json()) as { matches?: OsmPlaceMatch[] };
      setPlaceResults(body.matches ?? []);
    } catch {
      setPlaceError(t('courseConfig.osmMapPlaceFailed'));
    } finally {
      setPlaceSearching(false);
    }
  };

  const selectPlace = (place: OsmPlaceMatch) => {
    setPlaceResults(null);
    setPlaceQuery('');
    setGotoText('');
    setGotoInvalid(false);
    setLocateError(null);
    recentre({ lat: place.lat, lon: place.lon });
  };

  const goToMyLocation = async () => {
    setLocating(true);
    setLocateError(null);
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        setLocateError(t('courseConfig.osmMapLocateDenied'));
        return;
      }
      const position = await Location.getCurrentPositionAsync({});
      setGotoText('');
      setGotoInvalid(false);
      recentre({ lat: position.coords.latitude, lon: position.coords.longitude });
    } catch {
      setLocateError(t('courseConfig.osmMapLocateFailed'));
    } finally {
      setLocating(false);
    }
  };

  // Starts from the device's own position when nothing better is already known — a brand-new
  // course has no club position to fall back on, and the picker opening onto the whole world
  // is the one case worth a permission prompt without waiting for a tap. An already-known
  // anchor (club position, a previous search) is left alone: it is usually right, and jumping
  // to wherever the admin happens to be sitting would throw it away.
  useEffect(() => {
    if (initialCenter === null) {
      void goToMyLocation();
    }
    // Once, on open — not on every prop change, which would fight a pan or a typed coordinate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pan is stored in screen pixels measured against the *unzoomed* fit (see
  // applyViewTransform), so a fixed panX/panY represents fewer world degrees as zoom grows.
  // Left uncompensated, pressing + drags the crosshair's ground point back toward the map's
  // original centre instead of zooming in on it. Scaling the pan by the same ratio as the
  // zoom keeps the point under the crosshair fixed.
  // Pan is stored in screen pixels measured against the *unzoomed* fit (see
  // applyViewTransform), so a fixed panX/panY represents fewer world degrees as zoom grows.
  // Left uncompensated, pressing + drags the crosshair's ground point back toward the map's
  // original centre instead of zooming in on it. Scaling the pan by the same ratio as the
  // zoom keeps the point under the crosshair fixed.
  const stepZoom = (factor: number) =>
    setView((current) => {
      const zoom = Math.min(MAX_VIEW_ZOOM, Math.max(MIN_EDIT_ZOOM, current.zoom * factor));
      const ratio = zoom / current.zoom;
      return { zoom, panX: current.panX * ratio, panY: current.panY * ratio };
    });

  // Stable identity: HoleMapCanvas memoises its tile layout on this prop, and the crosshair
  // coordinate — read out of that same layout — feeds back into state below. A handler
  // recreated on every render would invalidate the memo every render too, and with state
  // downstream of it, that is a render loop rather than just a wasted recompute.
  const onPan = useCallback((pan: { panX: number; panY: number }) => {
    setView((current) => ({ ...current, ...pan }));
  }, []);

  return (
    <ScrollView
      testID="location-picker-scroll"
      style={styles.container}
      contentContainerStyle={styles.containerContent}
      keyboardShouldPersistTaps="handled"
    >
      <HoleMapCanvas
        tees={NO_TEES}
        green={null}
        loading={false}
        unavailable={false}
        fallbackCenter={frameCenter}
        fallbackSpanMeters={frameSpan}
        holdFallbackFrame
        view={view}
        onPan={onPan}
        showCenterCoordinate
        onCenterChange={setPickedPoint}
        minZoom={MIN_EDIT_ZOOM}
        canvasStyle={styles.canvas}
      />

      <View style={styles.zoomGroup}>
        <Pressable
          testID="location-picker-zoom-in"
          accessibilityRole="button"
          disabled={view.zoom >= MAX_VIEW_ZOOM}
          style={styles.zoomButton}
          onPress={() => stepZoom(ZOOM_STEP)}
        >
          <Text style={styles.buttonText}>+</Text>
        </Pressable>
        <Pressable
          testID="location-picker-zoom-out"
          accessibilityRole="button"
          disabled={view.zoom <= MIN_EDIT_ZOOM}
          style={styles.zoomButton}
          onPress={() => stepZoom(1 / ZOOM_STEP)}
        >
          <Text style={styles.buttonText}>−</Text>
        </Pressable>
        <Pressable
          testID="location-picker-zoom-fit"
          accessibilityRole="button"
          style={styles.zoomButton}
          onPress={() => setView(IDENTITY_VIEW)}
        >
          <Text style={styles.buttonText}>⤢</Text>
        </Pressable>
      </View>

      <View style={styles.gotoRow}>
        <TextInput
          testID="location-picker-place-input"
          style={styles.gotoInput}
          value={placeQuery}
          onChangeText={(text) => {
            setPlaceQuery(text);
            setPlaceError(null);
          }}
          onSubmitEditing={() => void searchPlace()}
          placeholder={t('courseConfig.osmMapPlacePlaceholder')}
          placeholderTextColor={appColors.text.muted}
          autoCorrect={false}
          inputMode="text"
          returnKeyType="search"
        />
        <Pressable
          testID="location-picker-place-search"
          accessibilityRole="button"
          disabled={placeSearching || placeQuery.trim().length === 0}
          style={[
            styles.toolButton,
            (placeSearching || placeQuery.trim().length === 0) && styles.toolButtonDisabled,
          ]}
          onPress={() => void searchPlace()}
        >
          {placeSearching ? (
            <ActivityIndicator color={appColors.text.primary} />
          ) : (
            <Ionicons name="search" size={16} color={appColors.text.primary} />
          )}
        </Pressable>
      </View>
      {placeError ? (
        <Text testID="location-picker-place-error" style={styles.errorText}>
          {placeError}
        </Text>
      ) : null}
      {placeResults ? (
        placeResults.length === 0 ? (
          <Text testID="location-picker-place-empty" style={styles.hint}>
            {t('courseConfig.osmMapPlaceNoResults')}
          </Text>
        ) : (
          <View testID="location-picker-place-results" style={styles.placeResults}>
            {placeResults.map((place, index) => (
              <Pressable
                key={`${place.name}-${place.lat}-${place.lon}-${index}`}
                testID={`location-picker-place-result-${index}`}
                accessibilityRole="button"
                style={styles.placeResultRow}
                onPress={() => selectPlace(place)}
              >
                <Text style={styles.placeResultName}>{place.name}</Text>
                {place.locality ? <Text style={styles.placeResultLocality}>{place.locality}</Text> : null}
              </Pressable>
            ))}
          </View>
        )
      ) : null}

      <Pressable
        testID="location-picker-use-my-location"
        accessibilityRole="button"
        disabled={locating}
        style={[styles.toolButton, locating && styles.toolButtonDisabled]}
        onPress={() => void goToMyLocation()}
      >
        {locating ? (
          <ActivityIndicator color={appColors.text.primary} />
        ) : (
          <Ionicons name="locate" size={16} color={appColors.text.primary} />
        )}
        <Text style={styles.buttonText}>{t('courseConfig.osmMapUseMyLocation')}</Text>
      </Pressable>
      {locateError ? (
        <Text testID="location-picker-locate-error" style={styles.errorText}>
          {locateError}
        </Text>
      ) : null}

      <View style={styles.gotoRow}>
        <TextInput
          testID="location-picker-goto-input"
          style={styles.gotoInput}
          value={gotoText}
          onChangeText={(text) => {
            setGotoText(text);
            setGotoInvalid(false);
          }}
          onSubmitEditing={goToTypedCoordinate}
          placeholder={t('courseConfig.osmMapGotoPlaceholder')}
          placeholderTextColor={appColors.text.muted}
          autoCapitalize="none"
          autoCorrect={false}
          inputMode="text"
          returnKeyType="go"
        />
        <Pressable
          testID="location-picker-goto"
          accessibilityRole="button"
          disabled={gotoText.trim().length === 0}
          style={[styles.toolButton, gotoText.trim().length === 0 && styles.toolButtonDisabled]}
          onPress={goToTypedCoordinate}
        >
          <Text style={styles.buttonText}>{t('courseConfig.osmMapGo')}</Text>
        </Pressable>
      </View>
      {gotoInvalid ? (
        <Text testID="location-picker-goto-invalid" style={styles.errorText}>
          {t('courseConfig.osmMapGotoInvalid')}
        </Text>
      ) : null}

      <Text style={styles.hint}>{t('courseConfig.osmMapHint')}</Text>

      <Pressable
        testID="location-picker-use-as-position"
        accessibilityRole="button"
        disabled={!pickedPoint}
        style={[styles.osmMapPickerLink, !pickedPoint && styles.toolButtonDisabled]}
        onPress={() => pickedPoint && onUseAsPosition(pickedPoint)}
      >
        <Text style={styles.osmMapPickerLinkText}>{t('courseConfig.osmMapUseAsPosition')}</Text>
      </Pressable>

      <View style={styles.actionRow}>
        <Pressable
          testID="location-picker-cancel"
          accessibilityRole="button"
          style={styles.secondaryButton}
          onPress={onCancel}
        >
          <Text style={styles.buttonText}>{t('courseConfig.osmMapCancel')}</Text>
        </Pressable>
        <Pressable
          testID="location-picker-confirm"
          accessibilityRole="button"
          disabled={!pickedPoint}
          style={[styles.primaryButton, !pickedPoint && styles.toolButtonDisabled]}
          onPress={() => pickedPoint && onConfirm(pickedPoint)}
        >
          <Text style={styles.primaryButtonText}>{t('courseConfig.osmMapConfirm')}</Text>
        </Pressable>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  // A plain View clipped the footer — Cancel and Confirm — off the bottom of the card on a
  // narrow phone, where the square canvas alone can be most of the screen's height. Scrolling
  // is what makes them reachable rather than merely present in the tree.
  container: { flexShrink: 1 },
  containerContent: { gap: 10 },
  canvas: { flexShrink: 0 },
  zoomGroup: { flexDirection: 'row', gap: 6, alignSelf: 'flex-end' },
  zoomButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  toolButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  toolButtonDisabled: { opacity: 0.4 },
  gotoRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  gotoInput: {
    flex: 1,
    minWidth: 140,
    height: 38,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.base,
    color: appColors.text.primary,
    fontSize: 13,
    fontVariant: ['tabular-nums'],
  },
  hint: { color: 'rgba(226,232,240,0.7)', fontSize: 12 },
  errorText: { color: appColors.status.warning, fontSize: 12, lineHeight: 16 },
  placeResults: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.base,
    overflow: 'hidden',
  },
  placeResultRow: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.strong,
  },
  placeResultName: { color: appColors.text.primary, fontSize: 13, fontWeight: '600' },
  placeResultLocality: { color: 'rgba(226,232,240,0.7)', fontSize: 12, marginTop: 2 },
  osmMapPickerLink: { alignSelf: 'flex-start' },
  osmMapPickerLinkText: {
    color: appColors.accent.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  actionRow: { flexDirection: 'row', gap: 10, justifyContent: 'flex-end' },
  secondaryButton: {
    paddingVertical: 11,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  primaryButton: {
    paddingVertical: 11,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.surface.alt,
  },
  buttonText: { color: appColors.text.primary, fontSize: 14, fontWeight: '600' },
  primaryButtonText: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
});
